import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../../../shared/components/empty-panel/EmptyPanel';
import useExport from '../../../../../../../shared/hooks/global/UseExport';
import usePermission from '../../../../../../../shared/hooks/global/UsePermission';
import { createCustomColumn, modelTemplate } from '../../../../../../../shared/helpers/ColumnHelper';
import {
  calculatePercentageShare,
  getAllSourcesCount,
} from '../../../../../../../shared/helpers/StatsHelpers';
import {
  filterSourcesByDomain,
  findSelectedDomain,
  getDomainsOptions,
} from '../../helpers/StatsHelper';
import { LinksData } from '../../models/StatsModel';
import { SourceDataResponse } from '../../../../../../../shared/models/monitor/data/sources/SourceDataResponse';
import { getSourceHref } from '../../../../../../../shared/utils/urls';
import useIsMobile from '../../../../../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../../../../../shared/utils/mobile';

interface KeySourcesProps {
  sourcesData: SourceDataResponse | null;
  /** Model filter from ?models= (same set the page passes to the API calls). */
  allowedMonitorTypes: Set<string> | null;
  /** CSV export of the prompt's sources (from useExportPromptData) — same as the classic view. */
  exportSources: () => Promise<void>;
  /** Hide the export button for demo prompts (matches the classic view). */
  isDemo: boolean;
}

/** "Key Sources" table — the classic prompt-view Link Tracking section, scoped to V2. */
const KeySources: React.FC<KeySourcesProps> = ({
  sourcesData,
  allowedMonitorTypes,
  exportSources,
  isDemo,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { canExport } = useExport();
  const projectsAccess = usePermission('PROJECTS');
  const [domain, setDomain] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleExportSources = async () => {
    if (isDownloading || !canExport) return;
    setIsDownloading(true);
    try {
      await exportSources();
    } finally {
      setIsDownloading(false);
    }
  };

  const sources: LinksData[] = sourcesData?.linksData ?? [];
  const domains: string[] = sourcesData?.domains ?? [];

  const modelFilteredSources = useMemo(() => {
    if (!allowedMonitorTypes) return sources;
    return sources.filter((s) => s.models.some((m) => allowedMonitorTypes.has(m)));
  }, [sources, allowedMonitorTypes]);

  const filteredSources = useMemo(
    () => filterSourcesByDomain(domain, modelFilteredSources),
    [modelFilteredSources, domain]
  );

  const domainsOptions = useMemo(() => getDomainsOptions(domains, domain), [domains, domain, t]);
  const selectedOption = useMemo(
    () => findSelectedDomain(domain, domainsOptions),
    [domain, domainsOptions]
  );

  const allSourcesCount = getAllSourcesCount(modelFilteredSources);

  if (!sourcesData) {
    return null;
  }

  const indexTemplate = (_rowData: LinksData, { rowIndex }: { rowIndex: number }) => `#${rowIndex + 1}`;

  const urlTemplate = (rowData: LinksData) => (
    <div className="source-data">
      <span
        className="source-data__icon"
        style={{ backgroundImage: `url("https://favicon.is/${rowData.url}")` }}
      />
      <a href={getSourceHref(rowData.url)} target="_blank" rel="noopener noreferrer">
        {rowData.url}
      </a>
    </div>
  );

  return (
    <>
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('linkTracking')}
          <CustomTooltip tooltipText="linkTrackingTooltip" />
        </div>
        <div className="page-box__source-filter">
          <Dropdown
            value={selectedOption}
            placeholder={t('selectDomain')}
            onChange={(e) => setDomain(e.value.name === t('allDomains') ? null : e.value.name)}
            options={domainsOptions}
            className="dropdown"
            optionLabel="name"
            optionValue="type"
            filter
          />
          {/* Eksport jest dla gościa zamknięty na backendzie (`validPermissionsForDownload` +
              domyślna odmowa) — przycisk kończyłby się 403, więc go nie pokazujemy. Przy pustej tabeli nie ma czego eksportować. */}
          {!isDemo && projectsAccess.write && filteredSources.length > 0 && (
            <>
              <Button
                id="btn-prompt-key-sources-export"
                className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
                onClick={handleExportSources}
              >
                {isDownloading ? (
                  <span className="ico--loader ico--xs" />
                ) : (
                  <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
                )}
              </Button>
              <CustomTooltip
                tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadSources'}
                targetRef="btn-prompt-key-sources-export"
              />
            </>
          )}
        </div>
      </div>
      <DataTable
        stripedRows
        paginator
        responsiveLayout="stack"
        paginatorClassName="pagination"
        removableSort
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        value={filteredSources}
        emptyMessage={() => <EmptyPanel />}
        tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
        tableClassName="table"
        className="table-wrapper table-wrapper--stack prompt-key-sources"
        currentPageReportTemplate={t('rowsNumber') + ':'}
        paginatorTemplate={
          isMobile
            ? MOBILE_PAGINATOR_TEMPLATE
            : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        }
        pageLinkSize={isMobile ? 3 : 5}
      >
        <Column
          field="index"
          header={t('position')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center table__cell--stack-rank"
          style={{ width: '50px' }}
          body={indexTemplate}
        />
        <Column
          field="url"
          header={t('url')}
          headerClassName="table__header"
          bodyClassName="table__cell--stack-title"
          style={{ maxWidth: '400px' }}
          body={urlTemplate}
        />
        <Column
          field="model"
          header={t('model')}
          headerClassName="table__header"
          style={{ width: '200px' }}
          body={modelTemplate}
        />
        {createCustomColumn({
          plainHeader: isMobile,
          field: 'count',
          body: (data: LinksData) => calculatePercentageShare(data.count, allSourcesCount),
          headerName: 'coverage',
          tooltip: t('coverageTooltip'),
          headerClassName: 'table__header table__header--center',
          bodyClassName: 'table__cell table__cell--center',
          style: { width: '50px' },
        })}
      </DataTable>
    </>
  );
};

export default KeySources;
